import { triggerNotification } from "components/TheComponents/Notification";
import { useAppContext } from "contexts";
import { IUserDto } from "interfaces";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { ExceptionHandlingType } from "types/apiServiceTypes";

export interface ExternalConnection {
  platform: string;
  username: string;
}

export enum EUserConnection {
  DISCORD = "discord",
}

export type UserConnectionStatus = {
  type: EUserConnection;
  isDisabled: boolean;
};

export type UserConnectionStatusResponse = {
  connections: ExternalConnection[];
  statuses: UserConnectionStatus[];
};

export type UseConnectionsTabReturn = {
  unlinkDiscord: () => Promise<void>;
  unlinkSteam: () => Promise<void>;
  verifyDiscord: () => Promise<void>;
  unlinkPlatform: ({ platform, otp }: { platform: string; otp: string }) => Promise<void>;
  isPlatformCanConnect: (platform: string) => Promise<boolean>;
  isLoading: boolean;
  showVerifyModal: boolean;
  platform: string | null;
  setPlatform: Dispatch<SetStateAction<string | null>>;
  kickUsername?: string;
  discordUsername?: string;
  steamUsername?: string;
  isDiscordLinked?: boolean;
  isSteamLinked?: boolean;
  idDiscordAPIDisabled?: boolean;
  hasVerifiedRoleOnDiscordGuild?: boolean;
  isVerifyLoading?: boolean;
};

export const useConnectionsTab = (user: IUserDto): UseConnectionsTabReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showVerifyModal, setShowVerifyModal] = useState<boolean>(false);
  const [externalConnections, setExternalConnections] = useState<ExternalConnection[]>([]);
  const [externalConnectionsState, setExternalConnectionsState] = useState<UserConnectionStatus[]>(
    []
  );
  const [platform, setPlatform] = useState<string | null>(null);
  const [isVerifyLoading, setIsVerifyLoading] = useState<boolean>(false);

  const { accessFetch } = useAppContext();

  const isPlatformCanConnect = useCallback(
    async (_platform: string): Promise<boolean> => {
      try {
        const hypedropCodeResponse = await accessFetch(
          `/user/connection-code?platform=${_platform}`
        );
        const hypedropCodeData = await hypedropCodeResponse?.json();
        if (hypedropCodeData.code === "bot is busy") {
          const capitalizePlatform = _platform.charAt(0).toUpperCase() + _platform.slice(1);
          triggerNotification({
            text: `${capitalizePlatform} verification is currently not available`,
            type: "error",
          });
          return false;
        }
        return true;
      } catch (error) {
        console.error("Error fetching data:", error);
        return false;
      }
    },
    [accessFetch]
  );

  const getConnections = useCallback(async () => {
    try {
      if (user.id) {
        setIsLoading(true);
        const connectionsResponse = await accessFetch(`/user/${user.id}/connections-status`);
        const data: UserConnectionStatusResponse = await connectionsResponse?.json();

        setExternalConnections(data.connections);
        setExternalConnectionsState(data.statuses);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [accessFetch, user.id]);

  const unlinkPlatform = useCallback(
    async ({ platform: _platform, otp }: { platform: string; otp: string }) => {
      try {
        setIsLoading(true);
        const unlinkResponse = await accessFetch(`/user/${user.id}/unlink-external-platform`, {
          method: "PATCH",
          headers: {
            "x-otp": otp,
          },
          body: JSON.stringify({
            platform: _platform.toLowerCase(),
          }),
        });
        const json = await unlinkResponse?.json();
        if (unlinkResponse && (unlinkResponse.status === 200 || unlinkResponse.status === 201)) {
          triggerNotification({
            text: `${_platform} unlinked`,
            type: "success",
          });
          getConnections();
          setShowVerifyModal(false);
          setPlatform(null);
        } else {
          triggerNotification({ text: json?.message, type: "error" });
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },
    [accessFetch, user.id, getConnections]
  );

  useEffect(() => {
    getConnections();
  }, [getConnections]);

  const unlinkDiscord = useCallback(async () => {
    try {
      setIsLoading(true);
      const unlinkResponse = await accessFetch(
        `/user/${user.id}/unlink-discord`,
        {
          method: "PATCH",
        },
        ExceptionHandlingType.AUTOMATIC
      );
      const json = await unlinkResponse?.json();

      if (unlinkResponse && (unlinkResponse.status === 200 || unlinkResponse.status === 201)) {
        setIsLoading(false);
        triggerNotification({ text: "Discord unlinked", type: "success" });
        // fetchUser();
      } else {
        triggerNotification({ text: json?.message, type: "error" });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [accessFetch, user.id]);

  const unlinkSteam = useCallback(async () => {
    try {
      setIsLoading(true);
      const unlinkResponse = await accessFetch(
        `/user/${user.id}/unlink-steam`,
        {
          method: "PATCH",
        },
        ExceptionHandlingType.AUTOMATIC
      );
      const json = await unlinkResponse?.json();

      if (unlinkResponse && (unlinkResponse.status === 200 || unlinkResponse.status === 201)) {
        setIsLoading(false);
        triggerNotification({ text: "Steam unlinked", type: "success" });
        // fetchUser();
      } else {
        triggerNotification({ text: json?.message, type: "error" });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [accessFetch, user.id]);

  const verifyDiscord = useCallback(async () => {
    try {
      setIsVerifyLoading(true);
      const verifyResponse = await accessFetch(
        `/user/${user.id}/verify-discord`,
        {},
        ExceptionHandlingType.AUTOMATIC
      );
      const data = await verifyResponse?.json();

      triggerNotification({
        text: `Discord ${data?.result ? "" : " not "} verified`,
        type: data?.result ? "success" : "error",
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      triggerNotification({
        text: (error as any).message ?? "Error",
        type: "error",
      });
    } finally {
      setIsVerifyLoading(false);
    }
  }, [accessFetch, user]);

  const kickUsername = Array.isArray(externalConnections)
    ? externalConnections.find((el: ExternalConnection) => el?.platform === "kick")?.username
    : undefined;
  const discordUsername = user?.discordUsername ?? undefined;
  const steamUsername = `${user?.steamId}`;
  const isDiscordLinked = !!user?.discordId;
  const isSteamLinked = !!user?.steamId;
  const hasVerifiedRoleOnDiscordGuild = user?.hasVerifiedRoleOnDiscordGuild;
  const idDiscordAPIDisabled =
    Array.isArray(externalConnectionsState) &&
    externalConnectionsState.find((cs) => cs.type === EUserConnection.DISCORD)?.isDisabled;

  return {
    unlinkSteam,
    unlinkDiscord,
    verifyDiscord,
    isLoading,
    unlinkPlatform,
    isPlatformCanConnect,
    showVerifyModal,
    platform,
    setPlatform,
    isVerifyLoading,
    idDiscordAPIDisabled,
    kickUsername,
    discordUsername,
    isDiscordLinked,
    isSteamLinked,
    steamUsername,
    hasVerifiedRoleOnDiscordGuild,
  };
};
